// Global scroll-to-top on route changes.
//
// React Router v6 deliberately does NOT reset scroll on navigation
// (https://reactrouter.com/en/main/components/scroll-restoration) — apps are
// expected to opt in. Mount this component once inside the <BrowserRouter>
// and every pathname / search change scrolls the window to the top.
//
// Exceptions:
//   * Hash-only changes (`#section`) are passed through so in-page anchor
//     links keep working.
//   * Browser Back / Forward (navigationType === "POP") preserves the user's
//     prior scroll position — that's the standard expectation.
//
// Pure side-effect component — renders nothing.

import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

export default function ScrollToTop() {
  const { pathname, search, hash, state } = useLocation();
  const navigationType = useNavigationType();   // "PUSH" | "REPLACE" | "POP"
  const prevRef = useRef({ pathname, search });

  useEffect(() => {
    const prev = prevRef.current;
    const pathOrSearchChanged =
      prev.pathname !== pathname || prev.search !== search;
    prevRef.current = { pathname, search };

    // Preserve scroll on Back / Forward so users return to where they were.
    if (navigationType === "POP") return;

    // D1/D2 root-cause fix (Five-Defect Correction Pass) — this effect's
    // dependency array includes `navigationType`, so an ordinary Save
    // handler calling `navigate(sameUrl, { replace: true })` (to refresh
    // local state / attach a `saved` flag, or to clear Reference Library
    // return-context state) changed navigationType from its previous
    // value to "REPLACE"/"PUSH" and re-ran this effect even though
    // pathname+search were unchanged. Skip whenever pathname+search are
    // genuinely unchanged — this also covers the original "hash-only
    // change" exception (in-page anchor links), since that case has
    // pathOrSearchChanged === false too.
    if (!pathOrSearchChanged) return;

    // Reset both the window AND the documentElement (some mobile browsers
    // track these separately) to the top. `behavior: "instant"` keeps the
    // transition snappy on every device.
    if (typeof window === "undefined") return;
    // Restore-position exception (ticket 237121, item 1; extended for the
    // Well Identity P0 correction) — a caller that navigates with
    // `state: { restoreScrollY }` gets its prior pixel scroll position
    // back instead of being reset to the top. A caller that instead
    // navigates with `state: { restoreAnchorId }` gets scrolled to that
    // DOM element instead — element-anchored, not pixel-based, so it stays
    // correct even when the destination page's layout differs from the
    // page the operator left (e.g. WellProfileEditor's brand-new-well
    // first save, where ContinueSetupPanel only mounts once the well is
    // no longer new, shifting everything below it by a fixed amount no
    // captured pixel offset can predict).
    const restoreAnchorId = (state && typeof state.restoreAnchorId === "string") ? state.restoreAnchorId : null;
    const restoreY = (state && typeof state.restoreScrollY === "number") ? state.restoreScrollY : 0;
    const apply = () => {
      if (restoreAnchorId) {
        const el = document.getElementById(restoreAnchorId);
        if (el) el.scrollIntoView({ behavior: "instant", block: "start" });
        return;
      }
      window.scrollTo({ top: restoreY, left: 0, behavior: "instant" });
      if (document?.documentElement) document.documentElement.scrollTop = restoreY;
      if (document?.body)            document.body.scrollTop = restoreY;
    };
    apply();
    // Keep re-asserting the target for ~700ms (ticket 237121, item 1).
    // Measured live: the destination page mounts with default/empty state,
    // then its own effects load the real record and can grow/shift the
    // layout — sometimes across more than one animation frame, and in dev
    // StrictMode, component effects run twice. Any of these can silently
    // move the scroll position again after a single scrollTo() "wins" the
    // first frame. A short-lived rAF loop keeps re-applying the target on
    // every frame instead of guessing how many retries are enough, so it
    // wins regardless of when the last layout shift lands. Cleaned up on
    // unmount/dep-change either way, so it can never run past its own
    // navigation or leak into the next one.
    let raf; let cancelled = false;
    const start = performance.now();
    const loop = () => {
      if (cancelled) return;
      apply();
      if (performance.now() - start < 700) raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelled = true; if (raf) cancelAnimationFrame(raf); };
  }, [pathname, search, hash, navigationType, state]);

  return null;
}
